import { useEffect, useRef, useState } from "react";

import {
  accountErrorMessage,
  listConnectedDevices,
  revokeConnectedDevice,
  type ConnectedDevicesState,
} from "../../lib/account-client";

export interface ConnectedDevicesApi {
  listConnectedDevices: typeof listConnectedDevices;
  revokeConnectedDevice: typeof revokeConnectedDevice;
}

export const connectedDevicesApi: ConnectedDevicesApi = { listConnectedDevices, revokeConnectedDevice };

export function ConnectedDevices({
  workspaceId,
  api = connectedDevicesApi,
}: {
  workspaceId: string;
  api?: ConnectedDevicesApi;
}) {
  const [state, setState] = useState<ConnectedDevicesState | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const errorRef = useRef<HTMLParagraphElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const returnRef = useRef<HTMLButtonElement>(null);

  const load = async () => {
    const next = await api.listConnectedDevices(workspaceId);
    setState(next);
    return next;
  };
  useEffect(() => {
    let active = true;
    void api.listConnectedDevices(workspaceId)
      .then((next) => { if (active) setState(next); })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load connected devices."));
      });
    return () => { active = false; };
  }, [api.listConnectedDevices, workspaceId]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (revokeId) confirmRef.current?.focus(); }, [revokeId]);

  if (!state && !error) return <p role="status">Loading connected devices...</p>;
  if (!state) return <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>;
  const selected = revokeId ? state.devices.find((device) => device.id === revokeId) ?? null : null;
  const revoke = async () => {
    if (!selected) return;
    setPending(true);
    setError(null);
    try {
      await api.revokeConnectedDevice(workspaceId, selected.id);
      await load();
      setRevokeId(null);
      setAnnouncement(`${selected.label} was revoked. Its tokens, scopes, and source authority are no longer active.`);
      returnRef.current?.focus();
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not revoke this device."));
      setAnnouncement("The device was not revoked.");
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="workspace-section compact-section review-stack" aria-labelledby={`connected-devices-${workspaceId}`}>
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      <div>
        <p className="step-label">Device access</p>
        <h2 id={`connected-devices-${workspaceId}`}>Connected devices</h2>
        <p>Devices can sync and inspect drafts. Publishing, credentials, public-risk approval, and suspension recovery stay in Doorway web.</p>
      </div>
      {error ? <p ref={errorRef} tabIndex={-1} role="alert">{error}</p> : null}
      {state.devices.length === 0 ? <p>No connected devices.</p> : (
        <ul>{state.devices.map((device) => (
          <li key={device.id}>
            <div>
              <strong>{device.label}</strong>
              <span>{device.status} · registered by {device.registeredBy.name} · key {device.keyFingerprint}</span>
              <span>{device.scopes.map((scope) => scope.kind === "workspace"
                ? `Entire workspace (${scope.status})`
                : `${scope.projectName ?? scope.projectId ?? "Project"} (${scope.status})`).join(", ") || "No active scope"}</span>
            </div>
            {device.canRevoke ? (
              <button
                ref={revokeId === null ? returnRef : undefined}
                type="button"
                disabled={pending}
                onClick={(event) => {
                  returnRef.current = event.currentTarget;
                  setRevokeId(device.id);
                }}
              >Revoke {device.label}</button>
            ) : null}
          </li>
        ))}</ul>
      )}
      {selected ? (
        <div role="alertdialog" aria-modal="true" aria-labelledby={`revoke-device-${selected.id}`}>
          <h3 id={`revoke-device-${selected.id}`}>Revoke {selected.label}?</h3>
          <p>All device tokens, project scopes, and source identities will stop working immediately. The device must be linked again.</p>
          <button ref={confirmRef} type="button" disabled={pending} onClick={() => void revoke()}>Confirm device revocation</button>
          <button type="button" disabled={pending} onClick={() => {
            setRevokeId(null);
            returnRef.current?.focus();
          }}>Cancel</button>
        </div>
      ) : null}
    </section>
  );
}
